/**
 * Space Lifecycle Commands - Space-level operations
 *
 * Handles creating, opening, switching, renaming, and listing spaces. These commands
 * operate on the Space entity itself (not windows inside it). Coordinates between
 * systemStore (cache + backend), entityStore (tree visibility), spaceStore (active
 * space state), and react-router (URL / navigation).
 *
 * Following ENTITY_STATE_ARCHITECTURE pattern.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import { navigateTo } from '../../utils/navigation';
import useSystemStore from '../../stores/systemStore';
import { spaceService } from '../../services/spaceService';

const log = createFileLogger('SPACE_LIFECYCLE_COMMANDS', { enabled: false });

export const spaceCreateCommand: SelfContainedCommand = {
  id: 'space_create',
  description: 'Create a new space in the current dataspace and (by default) navigate to it',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Name for the new space',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Space name cannot be empty';
          if (value.length > 100) return 'Space name too long (max 100 chars)';
          return null;
        },
        examples: ['My Workspace', 'Project Alpha', 'Research Notes']
      },
      {
        name: 'templateType',
        type: 'string',
        description: 'Optional template type to initialize the space with',
        required: false,
        examples: ['blank', 'research', 'project']
      },
      {
        name: 'autoOpen',
        type: 'boolean',
        description: 'Navigate to the new space after creation. Defaults to true. Set false when creating a space in the background (e.g. from chat).',
        required: false,
        defaultValue: true,
        examples: [true, false]
      }
    ]
  },

  examples: {
    good: [
      {
        input: { name: 'My Workspace' },
        expectedOutput: createSuccessResult('Space created', { spaceId: 'space_123' }),
        description: 'Create a new blank space and navigate to it',
        context: 'When user wants a new workspace'
      },
      {
        input: { name: 'Background Research', autoOpen: false },
        expectedOutput: createSuccessResult('Space created', { spaceId: 'space_456' }),
        description: 'Create a space without navigating away from the current one',
        context: 'When spinning up a project from a chat and keeping the user in place'
      }
    ],
    bad: [
      {
        input: { name: '' },
        error: 'Space name cannot be empty',
        why: 'Empty names are not valid',
        fix: 'Provide a meaningful space name'
      }
    ]
  },

  performance: {
    expectedDuration: 500,
    complexity: 'medium'
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { name, templateType, autoOpen = true } = params;

    try {
      log.info('[space_create] Creating space:', { name, templateType, autoOpen });

      const newSpace = await useSystemStore.getState().actions.createSpace({
        name,
        templateType
      });

      log.info('[space_create] Space created:', {
        spaceId: newSpace.id,
        name: newSpace.name
      });

      if (autoOpen) {
        navigateTo(`/spaces/${newSpace.id}`);
        log.info('[space_create] Navigated to new space');
      }

      return createSuccessResult(
        autoOpen ? `Space created and opened: ${newSpace.name}` : `Space created: ${newSpace.name}`,
        {
          spaceId: newSpace.id,
          space: {
            id: newSpace.id,
            name: newSpace.name,
            org_id: newSpace.org_id
          },
          opened: !!autoOpen
        },
        {
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    } catch (error: any) {
      log.error('[space_create] Failed to create space:', error);
      return createErrorResult(
        'Failed to create space',
        error.message || 'Unknown error',
        'execution',
        {
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// Shared handler for space_open and space_switch — both navigate to /spaces/:id.
// space_switch is kept as an alias for backwards compatibility with older docs.
async function openSpaceHandler(params: any, startTime: number): Promise<StandardToolResult> {
  const { spaceId } = params;

  if (!spaceId || typeof spaceId !== 'string') {
    return createErrorResult(
      'Invalid spaceId',
      'spaceId is required and must be a string',
      'validation',
      { metadata: { executionTime: Date.now() - startTime } }
    );
  }

  try {
    log.info('[space_open] Opening space:', { spaceId });

    // Best-effort pre-check: verify the space exists and is in the current dataspace.
    // If the cache doesn't have it, refresh once. We still navigate even if the check
    // fails, because SpacePageWrapper will render a loader + access-denied UI for
    // invalid IDs — duplicating that error path here would be lying to the user.
    let space = useSystemStore.getState().getSpaceById(spaceId);
    if (!space) {
      try {
        await useSystemStore.getState().actions.refreshRecentSpaces();
        space = useSystemStore.getState().getSpaceById(spaceId);
      } catch (refreshErr) {
        log.warn('[space_open] refreshRecentSpaces failed, navigating anyway:', refreshErr);
      }
    }

    useSystemStore.getState().actions.switchSpace(spaceId);
    navigateTo(`/spaces/${spaceId}`);

    log.info('[space_open] Navigated to space:', spaceId);

    return createSuccessResult(
      space ? `Opened space: ${space.name}` : `Opened space`,
      {
        spaceId,
        space: space ? {
          id: space.id,
          name: space.name,
          org_id: space.org_id,
        } : undefined,
      },
      {
        metadata: { executionTime: Date.now() - startTime },
        ui: { refreshContext: true, resultPreview: 'minimal' }
      }
    );
  } catch (error: any) {
    log.error('[space_open] Failed to open space:', error);
    return createErrorResult(
      'Failed to open space',
      error.message || 'Unknown error',
      'execution',
      { metadata: { executionTime: Date.now() - startTime } }
    );
  }
}

export const spaceOpenCommand: SelfContainedCommand = {
  id: 'space_open',
  description: 'Navigate to an existing space by ID (must be in the current dataspace)',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'spaceId',
        type: 'string',
        description: 'ID of the space to open',
        required: true,
        examples: ['68123abc456def789012345', 'space_123']
      }
    ]
  },

  examples: {
    good: [
      {
        input: { spaceId: '68123abc456def789012345' },
        expectedOutput: createSuccessResult('Opened space', { spaceId: '68123abc456def789012345' }),
        description: 'Navigate to a known space',
        context: 'When the user asks to open or go to a specific space'
      }
    ],
    bad: [
      {
        input: { spaceId: '' },
        error: 'Invalid spaceId',
        why: 'spaceId is required',
        fix: 'Pass the space ID from space_list'
      }
    ]
  },

  performance: { expectedDuration: 200, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    return openSpaceHandler(params, startTime);
  }
};

export const spaceSwitchCommand: SelfContainedCommand = {
  id: 'space_switch',
  description: 'Alias for space_open — navigate to a different space by ID',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'spaceId',
        type: 'string',
        description: 'ID of the space to switch to',
        required: true
      }
    ]
  },

  examples: { good: [], bad: [] },
  performance: { expectedDuration: 200, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    return openSpaceHandler(params, startTime);
  }
};

export const spaceRenameCommand: SelfContainedCommand = {
  id: 'space_rename',
  description: 'Rename a space. Works on any space in the current dataspace, not just the active one.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'spaceId',
        type: 'string',
        description: 'ID of the space to rename',
        required: true,
        examples: ['68123abc456def789012345']
      },
      {
        name: 'name',
        type: 'string',
        description: 'New name for the space',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Space name cannot be empty';
          if (value.length > 100) return 'Space name too long (max 100 chars)';
          return null;
        },
        examples: ['Q2 Planning', 'Client Onboarding']
      }
    ]
  },

  examples: {
    good: [
      {
        input: { spaceId: '68123abc456def789012345', name: 'Q2 Planning' },
        expectedOutput: createSuccessResult('Space renamed', { spaceId: '68123abc456def789012345', name: 'Q2 Planning' }),
        description: 'Rename a space',
        context: 'When the user asks to rename a space or project'
      }
    ],
    bad: [
      {
        input: { spaceId: 'abc', name: '' },
        error: 'Space name cannot be empty',
        why: 'Names must be non-empty',
        fix: 'Provide a non-empty name'
      }
    ]
  },

  performance: { expectedDuration: 400, complexity: 'medium' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { spaceId, name } = params;

    try {
      const trimmed = (name ?? '').trim();
      if (!spaceId || !trimmed) {
        return createErrorResult(
          'Invalid arguments',
          'spaceId and non-empty name are required',
          'validation',
          { metadata: { executionTime: Date.now() - startTime } }
        );
      }

      log.info('[space_rename] Renaming space:', { spaceId, name: trimmed });

      // 1. Persist to backend. This broadcasts the rename to the org Y.Doc room so
      //    teammates see it in real-time.
      await spaceService.updateSpaceName(spaceId, trimmed);

      // 2. Update systemStore cache so Home/SpacesMenu reflect the new name.
      //    Use _applyLocalSpaceUpdate directly to avoid a second backend call — actions.updateSpace
      //    would re-issue the PATCH because it checks updates.name !== existingSpace.name.
      try {
        const storeState = useSystemStore.getState();
        const spaceInCache = storeState.getSpaceById(spaceId);
        if (spaceInCache) {
          storeState._applyLocalSpaceUpdate({
            ...spaceInCache,
            name: trimmed,
            updatedAt: new Date().toISOString(),
          });
        }
      } catch (cacheErr) {
        log.warn('[space_rename] systemStore cache update failed (non-fatal):', cacheErr);
      }

      // 3. Update entityStore so the tree / Drive panel sees the new name immediately.
      //    The backend already broadcast the change via sync_entity_to_system, so we only
      //    need to patch the local cache — use the local-only updateEntity overload to avoid
      //    a redundant mergeUpdate backend call.
      try {
        const { default: useEntityStore } = await import('../../stores/entityStore');
        const entityStore = useEntityStore.getState();
        const spaceEntity = entityStore.entities.find((e: any) => e._id === spaceId || e.id === spaceId);
        if (spaceEntity) {
          entityStore.updateEntity({
            ...spaceEntity,
            name: trimmed,
            updated_at: new Date().toISOString(),
          });
        }
      } catch (entityErr) {
        log.warn('[space_rename] entityStore update failed (non-fatal):', entityErr);
      }

      // 4. If this is the currently-active space, update spaceStore so the header /
      //    breadcrumbs reflect the new name immediately.
      try {
        const { default: useSpaceStore } = await import('../../stores/spaceStore');
        useSpaceStore.setState((state: any) =>
          state.activeSpace && state.activeSpace.id === spaceId
            ? { activeSpace: { ...state.activeSpace, name: trimmed, updatedAt: new Date().toISOString() } }
            : {}
        );
      } catch (spaceStoreErr) {
        log.warn('[space_rename] spaceStore update failed (non-fatal):', spaceStoreErr);
      }

      log.info('[space_rename] Space renamed successfully');

      return createSuccessResult(
        `Space renamed to: ${trimmed}`,
        { spaceId, name: trimmed },
        {
          metadata: { executionTime: Date.now() - startTime },
          ui: { refreshContext: true, resultPreview: 'minimal' }
        }
      );
    } catch (error: any) {
      log.error('[space_rename] Failed to rename space:', error);
      return createErrorResult(
        'Failed to rename space',
        error.message || 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

export const spaceListCommand: SelfContainedCommand = {
  id: 'space_list',
  description: 'List recent spaces in the current dataspace',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: []
  },

  examples: {
    good: [
      {
        input: {},
        expectedOutput: createSuccessResult('Spaces listed', { spaces: [] }),
        description: 'List all spaces',
        context: 'When user wants to see available spaces'
      }
    ],
    bad: []
  },

  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },

  handler: async (_params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();

    try {
      log.info('[space_list] Listing spaces');

      await useSystemStore.getState().actions.refreshRecentSpaces();

      const spaces = useSystemStore.getState().getCurrentDataspaceSpaces();

      log.info('[space_list] Spaces listed:', spaces.length);

      return createSuccessResult(
        `Found ${spaces.length} space${spaces.length !== 1 ? 's' : ''}`,
        {
          spaces: spaces.map(s => ({
            id: s.id,
            name: s.name,
            org_id: s.org_id,
            updatedAt: s.updatedAt
          })),
          count: spaces.length
        },
        {
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    } catch (error: any) {
      log.error('[space_list] Failed to list spaces:', error);
      return createErrorResult(
        'Failed to list spaces',
        error.message || 'Unknown error',
        'execution',
        {
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

export const spaceLifecycleCommands: SelfContainedCommand[] = [
  spaceCreateCommand,
  spaceOpenCommand,
  spaceSwitchCommand,
  spaceRenameCommand,
  spaceListCommand,
];
